// Web Speech Recognition API ラッパー
// Lv3 で「アナウンス直後の15秒だけ録音」用途

// ブラウザ固有のSpeechRecognition型
interface SpeechRecognitionResult {
  transcript: string;
  confidence: number;
}

type SpeechRecognitionEvent = Event & {
  results: { [key: number]: { [key: number]: SpeechRecognitionResult; isFinal: boolean; length: number }; length: number };
};

declare global {
  interface Window {
    SpeechRecognition?: any;
    webkitSpeechRecognition?: any;
  }
}

export interface ListenResult {
  text: string;
  /** マイク無音 or タイムアウトだった場合true */
  silent: boolean;
  /** エラーの場合 */
  error?: string;
}

/**
 * 指定秒数だけマイクを開いて、子どもの応答を聞き取る
 * @param maxSeconds 最大録音秒数（既定15秒）
 */
export function listen(maxSeconds = 15): Promise<ListenResult> {
  return new Promise((resolve) => {
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    if (!SR) {
      resolve({ text: '', silent: true, error: 'このブラウザはSpeech Recognition非対応' });
      return;
    }

    const recognition = new SR();
    recognition.lang = 'ja-JP';
    recognition.continuous = false;
    recognition.interimResults = false;
    recognition.maxAlternatives = 1;

    let finished = false;
    let resultText = '';

    const finalize = (result: ListenResult) => {
      if (finished) return;
      finished = true;
      try {
        recognition.stop();
      } catch {
        /* noop */
      }
      resolve(result);
    };

    recognition.onresult = (e: SpeechRecognitionEvent) => {
      for (let i = 0; i < e.results.length; i++) {
        const r = e.results[i][0];
        if (r && r.transcript) resultText += r.transcript;
      }
      if (resultText.trim()) {
        finalize({ text: resultText.trim(), silent: false });
      }
    };

    recognition.onerror = (e: any) => {
      // no-speech は無音扱い
      if (e.error === 'no-speech' || e.error === 'aborted') {
        finalize({ text: '', silent: true });
      } else {
        finalize({ text: '', silent: true, error: e.error });
      }
    };

    recognition.onend = () => {
      if (!finished) {
        finalize({ text: resultText.trim(), silent: resultText.trim().length === 0 });
      }
    };

    // タイムアウト
    setTimeout(() => {
      finalize({ text: resultText.trim(), silent: resultText.trim().length === 0 });
    }, maxSeconds * 1000);

    try {
      recognition.start();
    } catch (err) {
      finalize({ text: '', silent: true, error: String(err) });
    }
  });
}

export function isSttAvailable(): boolean {
  return !!(window.SpeechRecognition || window.webkitSpeechRecognition);
}
